/**
 * Gap-flow / reattachment GEOMETRIC metrics for the deployed multi-element high-lift section
 * (FXC-9009, Phase 3). These are the optimizable OUTPUTS Harry asked for: "optimise on geometric
 * settings for ... gap flow and/or reattachment". They are PURE functions of the deployed element
 * contours (the same `Configuration` the geometry engine produces), so they cost nothing to
 * evaluate and can be swept/optimized by Tailor alongside (or as cheap proxies for) the RANS CL/CD.
 *
 * A slot is the channel between an UPSTREAM element's trailing region and a DOWNSTREAM element's
 * leading nose. For this 3-element section the two slots are main→vane and vane→flap. For each we
 * report:
 *   - gap        : slot gap width — the throat, min distance from the upstream TE to the downstream
 *                  LE nose. The single most-cited slot parameter (drives the slot jet velocity).
 *   - overlap    : chordwise overlap — how far the downstream LE sits FORWARD (smaller x) of the
 *                  upstream TE. Positive = overlapping (Fowler-like), negative = a clean gap.
 *   - exitWidth  : gap-exit width — the slot opening at its downstream end (the slot mouth on the
 *                  suction side), the 2-D analogue of the gap-exit area (per unit span).
 *   - convergence: slot convergence ratio = exitWidth / gap. <1 ⇒ a converging (accelerating) slot,
 *                  the high-lift design intent; >1 ⇒ diffusing (separation-prone).
 *   - reattachOffset : reattachment proxy — signed normal offset of the downstream LE from the
 *                  upstream WAKE LINE (the ray leaving the upstream TE along its local surface
 *                  direction). Negative ⇒ the downstream nose sits BELOW the wake (immersed in the
 *                  shear layer → encourages reattachment); positive ⇒ above it (exposed).
 *   - noseCurvature : downstream LE curvature (1/radius, chord^-1) — a blunter nose (lower
 *                  curvature) tolerates the slot jet's incidence better before separating.
 *
 * Conventions match the rest of the tool: points are [x, y]; x aft, y up.
 */
import type { V2 } from './estolConfig';

const sub = (a: V2, b: V2): V2 => [a[0] - b[0], a[1] - b[1]];
const norm = (v: V2): number => Math.hypot(v[0], v[1]);
const dot = (a: V2, b: V2): number => a[0] * b[0] + a[1] * b[1];
const unit = (v: V2): V2 => {
  const m = norm(v);
  return m > 0 ? [v[0] / m, v[1] / m] : [0, 0];
};

/** Index of the point with the smallest x (the leading edge of an element). */
function leadingEdgeIdx(contour: V2[]): number {
  let k = 0;
  for (let i = 1; i < contour.length; i++) if ((contour[i]?.[0] ?? Infinity) < (contour[k]?.[0] ?? Infinity)) k = i;
  return k;
}

/** Index of the point with the largest x (the aftmost / trailing-edge point of an element). */
function trailingEdgeIdx(contour: V2[]): number {
  let k = 0;
  for (let i = 1; i < contour.length; i++) if ((contour[i]?.[0] ?? -Infinity) > (contour[k]?.[0] ?? -Infinity)) k = i;
  return k;
}

/** Minimum distance from `p` to any vertex of `poly`, with the index of the closest vertex. */
function nearestVertex(p: V2, poly: V2[]): { dist: number; idx: number } {
  let best = Infinity;
  let idx = 0;
  for (let i = 0; i < poly.length; i++) {
    const pt = poly[i];
    if (!pt) continue;
    const d = norm(sub(pt, p));
    if (d < best) {
      best = d;
      idx = i;
    }
  }
  return { dist: best, idx };
}

/** Minimum vertex-to-vertex distance between two contours, with the contributing indices. */
export function closestApproach(a: V2[], b: V2[]): { dist: number; ia: number; ib: number } {
  let best = Infinity;
  let ia = 0;
  let ib = 0;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i];
    if (!ai) continue;
    const { dist, idx } = nearestVertex(ai, b);
    if (dist < best) {
      best = dist;
      ia = i;
      ib = idx;
    }
  }
  return { dist: best, ia, ib };
}

/**
 * Discrete curvature at vertex `i` of `poly` (the Menger curvature of the triangle formed by its
 * neighbours), in chord^-1. Returns 0 at the ends or for degenerate (collinear) triples.
 */
function vertexCurvature(poly: V2[], i: number, span = 3): number {
  const a = poly[Math.max(0, i - span)];
  const b = poly[i];
  const c = poly[Math.min(poly.length - 1, i + span)];
  if (!a || !b || !c || a === c) return 0;
  const ab = norm(sub(a, b));
  const bc = norm(sub(b, c));
  const ca = norm(sub(c, a));
  if (ab < 1e-9 || bc < 1e-9 || ca < 1e-9) return 0;
  // 2·area = |cross(b-a, c-a)|; curvature = 4·area / (|ab|·|bc|·|ca|).
  const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  return (2 * Math.abs(cross)) / (ab * bc * ca);
}

/**
 * Local "wake direction" leaving a trailing-edge vertex: the unit tangent of the contour at that
 * vertex (averaged over a small neighbourhood), oriented downstream (+x). This is the line the
 * upstream element's boundary layer leaves along — the reattachment reference.
 */
function wakeDirection(poly: V2[], teIdx: number, span = 3): V2 {
  const before = poly[Math.max(0, teIdx - span)];
  const after = poly[Math.min(poly.length - 1, teIdx + span)];
  const te = poly[teIdx];
  if (!before || !after || !te) return [1, 0];
  // Both neighbours are "behind" the TE along the contour; the chordwise tangent is the vector
  // from the more-forward neighbour toward the TE. Pick whichever neighbour is further forward.
  const fwd = before[0] < after[0] ? before : after;
  const dir = unit(sub(te, fwd));
  return dir[0] >= 0 ? dir : [-dir[0], -dir[1]];
}

/** Per-slot geometric metrics between an upstream and a downstream element. */
export interface SlotMetrics {
  /** Slot gap width (throat) — min upstream-TE-region → downstream-LE-nose distance (chords). */
  gap: number;
  /** Chordwise overlap (chords): upstreamTE.x − downstreamLE.x. Positive = overlapping. */
  overlap: number;
  /** Gap-exit width (chords): slot opening at the downstream (suction-side) end of the channel. */
  exitWidth: number;
  /** Slot convergence ratio = exitWidth / gap (dimensionless). <1 converging, >1 diffusing. */
  convergence: number;
  /** Reattachment proxy: signed normal offset of the downstream LE from the upstream wake line
   *  (chords). Negative ⇒ nose below the wake (immersed, reattachment-favourable). */
  reattachOffset: number;
  /** Downstream LE curvature (chord^-1): blunter (lower) noses tolerate slot-jet incidence better. */
  noseCurvature: number;
}

/**
 * Compute the slot metrics between `upstream` and `downstream` deployed contours. Pure; no mutation.
 * `upstream` is the forward element (e.g. main), `downstream` the aft one (e.g. vane) whose nose
 * sits in the slot. All quantities are chord-normalized.
 */
export function slotMetrics(upstream: V2[], downstream: V2[]): SlotMetrics {
  const upTeIdx = trailingEdgeIdx(upstream);
  const upTe = upstream[upTeIdx] ?? [0, 0];
  const dnLeIdx = leadingEdgeIdx(downstream);
  const dnLe = downstream[dnLeIdx] ?? [0, 0];

  // Gap (throat): closest approach between the upstream aft region and the downstream nose. We use
  // the full contours' closest approach, which finds the true minimum slot width regardless of
  // which exact vertices form the throat.
  const { dist: gap } = closestApproach(upstream, downstream);

  // Overlap: how far the downstream LE sits forward (smaller x) of the upstream TE.
  const overlap = upTe[0] - dnLe[0];

  // Exit width: the slot opening at its downstream end. We take it on the downstream side of the
  // throat — the gap measured from the upstream TE to the nearest downstream vertex AFT of the LE
  // (the suction-side wall of the slot). This is the slot mouth where the jet exits onto the
  // downstream upper surface.
  const aftOfLe = downstream
    .map((p, i) => ({ p, i }))
    .filter(({ p, i }) => i !== dnLeIdx && p[0] > dnLe[0]);
  const exitRef = aftOfLe.length ? nearestVertex(upTe, aftOfLe.map((q) => q.p)).dist : gap;
  const exitWidth = exitRef;
  const convergence = gap > 1e-9 ? exitWidth / gap : 0;

  // Reattachment proxy: signed normal distance of the downstream LE from the upstream wake line
  // (ray from the upstream TE along its local surface tangent). Sign: negative below the wake.
  const wake = wakeDirection(upstream, upTeIdx);
  const wakeNormal: V2 = [-wake[1], wake[0]]; // +y-ish normal
  const reattachOffset = dot(sub(dnLe, upTe), wakeNormal);

  const noseCurvature = vertexCurvature(downstream, dnLeIdx);

  return { gap, overlap, exitWidth, convergence, reattachOffset, noseCurvature };
}

/** The deployed contours the gap metrics read (subset of geometry.ts's `Configuration`).
 *
 *  Back-compat keys: the historical `main`/`vane`/`flaperon` triple still drives the
 *  `slot_mv_*`/`slot_vf_*` output aliases. The N-element generalization is driven by `elements`
 *  (the full deployed collection in chordwise order) when present — see {@link gapMetrics}. */
export interface DeployedContours {
  main: V2[];
  vane: V2[];
  flaperon: V2[];
  /** Optional FULL deployed-element list (id + contour). When present and longer than 2 wall
   *  elements, the metrics enumerate every consecutive chordwise slot (`slot_{i}{i+1}_*`) instead
   *  of just the fixed 2-slot main→vane / vane→flap pair. */
  elements?: { id: string; contour: V2[] }[];
}

/** One enumerated slot between two consecutive deployed elements (ordered LE x ascending). */
export interface EnumeratedSlot {
  /** Zero-based upstream element index (after chordwise ordering). */
  upstreamIdx: number;
  /** Upstream element id (for provenance / debugging). */
  upstreamId: string;
  /** Downstream element id. */
  downstreamId: string;
  metrics: SlotMetrics;
}

/** Both slots of the 3-element section, plus their named output map (the Tailor/ADB outputs).
 *
 *  `mainVane`/`vaneFlap` are the legacy fixed-triple slots (the Tailor axes `slot_mv_*`/`slot_vf_*`
 *  bind to these, so they are ALWAYS emitted for back-compat). `slots` is the generalized
 *  consecutive-slot enumeration over the full deployed collection (N-1 slots for N wall elements);
 *  for the default 3-element section it is exactly [main→vane, vane→flap]. */
export interface GapMetrics {
  /** Slot between the main element and the vane (the primary high-lift slot). */
  mainVane: SlotMetrics;
  /** Slot between the vane and the flaperon (the second slot). */
  vaneFlap: SlotMetrics;
  /** Generalized consecutive-element slots over the full deployed collection (chordwise order). */
  slots: EnumeratedSlot[];
}

/** Order wall contours by leading-edge x (chordwise), so consecutive entries form real slots. */
export function orderByLeadingEdge(
  elements: { id: string; contour: V2[] }[],
): { id: string; contour: V2[] }[] {
  return elements
    .filter((e) => Array.isArray(e.contour) && e.contour.length >= 3)
    .map((e) => ({ e, leX: e.contour[leadingEdgeIdx(e.contour)]?.[0] ?? Infinity }))
    .sort((a, b) => a.leX - b.leX)
    .map(({ e }) => e);
}

/**
 * Compute the gap metrics for a deployed configuration. Pure.
 *
 * The fixed `mainVane`/`vaneFlap` slots are always computed from the legacy triple (so the
 * `slot_mv_*`/`slot_vf_*` Tailor axes survive byte-for-byte). When the full `elements` collection is
 * supplied it is additionally ordered chordwise and every consecutive pair becomes an `EnumeratedSlot`
 * (emitted as `slot_{i}{i+1}_*`). For the default 3-element section the enumeration reproduces the
 * two legacy slots; extra elements (a 2nd vane, a slat) add further `slot_*` channels.
 */
export function gapMetrics(conf: DeployedContours): GapMetrics {
  const slots: EnumeratedSlot[] = [];
  if (conf.elements && conf.elements.length >= 2) {
    const ordered = orderByLeadingEdge(conf.elements);
    for (let i = 0; i + 1 < ordered.length; i++) {
      const up = ordered[i];
      const dn = ordered[i + 1];
      if (!up || !dn) continue;
      slots.push({
        upstreamIdx: i,
        upstreamId: up.id,
        downstreamId: dn.id,
        metrics: slotMetrics(up.contour, dn.contour),
      });
    }
  }
  return {
    mainVane: slotMetrics(conf.main, conf.vane),
    vaneFlap: slotMetrics(conf.vane, conf.flaperon),
    slots,
  };
}

/**
 * Flatten the gap metrics into the named scalar OUTPUTS that map onto Tailor/ADB axes (mirrors the
 * `point_outputs` naming on the server). These are appended to each run's value JSON so a design can
 * be optimized directly on slot geometry. Names are slot-prefixed: `slot_mv_*` (main→vane),
 * `slot_vf_*` (vane→flap) for the first two slots (legacy aliases — Tailor axes bind to these), plus
 * `slot_{i}{i+1}_*` for EVERY consecutive slot of the full deployed collection (FXC-9009 N-element
 * wire). For the default 3-element section, `slot_01_*`==`slot_mv_*` and `slot_12_*`==`slot_vf_*`.
 */
export function gapMetricOutputs(m: GapMetrics): Record<string, number> {
  const emit = (prefix: string, s: SlotMetrics): Record<string, number> => ({
    [`${prefix}_gap`]: s.gap,
    [`${prefix}_overlap`]: s.overlap,
    [`${prefix}_exit_width`]: s.exitWidth,
    [`${prefix}_convergence`]: s.convergence,
    [`${prefix}_reattach_offset`]: s.reattachOffset,
    [`${prefix}_nose_curvature`]: s.noseCurvature,
  });
  // Legacy 2-slot aliases (always present — existing Tailor axes bind to these).
  const out: Record<string, number> = { ...emit('slot_mv', m.mainVane), ...emit('slot_vf', m.vaneFlap) };
  // Generalized N-slot enumeration (chordwise consecutive pairs of the full collection).
  for (const slot of m.slots) {
    const i = slot.upstreamIdx;
    Object.assign(out, emit(`slot_${i}${i + 1}`, slot.metrics));
  }
  return out;
}
